import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import type { Moment } from 'moment';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useQueryClient } from '@tanstack/react-query';
import { useDialog } from '@/components/dialogs/useDialog';
import { usePermissions } from '@/auth/usePermissions';
import {
  useUserOperationsQuery,
  useUserOperationsStatsQuery,
  usePaymentIntermediariesQuery,
  USER_OPS_KEYS,
} from './api';
import { OperationDetailsDialog } from './OperationDetailsDialog';
import type { AddWithdrawFundsRecord } from '@/types/user-operations';
import { PaymentIntermediariesStatusEnum, OperationsStatusEnum } from '@/types/user-operations';

// ── constants ─────────────────────────────────────────────────────────────────

const FILTER_TYPES = [
  { key: 'ADD_FUNDS', labelKey: 'pages.userOperations.filterAddFunds', defaultLabel: 'Add Funds' },
  { key: 'WITHDRAW_FUNDS', labelKey: 'pages.userOperations.filterWithdrawFunds', defaultLabel: 'Withdraw Funds' },
  { key: 'ADDRESS_BOOK_TRANSFER', labelKey: 'pages.userOperations.filterBankTransfer', defaultLabel: 'Bank Transfer' },
  { key: 'WITHDRAW_REVENUE', labelKey: 'pages.userOperations.filterWithdrawRevenue', defaultLabel: 'Withdraw Revenue' },
] as const;

type SortField = 'reference' | 'date' | 'businessName' | 'status' | 'amount';
type SortDir = 'asc' | 'desc';

// ── helpers ───────────────────────────────────────────────────────────────────

function fmt(date: Moment): string {
  return date.format('YYYY-MM-DD');
}

function compare(a: string | number, b: string | number, asc: boolean): number {
  return (a < b ? -1 : 1) * (asc ? 1 : -1);
}

// ── Stats card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  count: number;
  contentKey: string;
  titleKey: string;
  defaultContent: string;
  defaultTitle: string;
  color: string;
  onClick: () => void;
}

function StatCard({ count, contentKey, titleKey, defaultContent, defaultTitle, color, onClick }: StatCardProps) {
  const { t } = useTranslation();
  return (
    <Card sx={{ flex: 1, minWidth: 180 }}>
      <CardContent sx={{ pb: 0 }}>
        <Typography variant="h4" color={color} fontWeight={700}>
          {count}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t(contentKey, { defaultValue: defaultContent })}
        </Typography>
      </CardContent>
      <CardActionArea
        onClick={onClick}
        sx={{ bgcolor: color, px: 2, py: 1, display: 'flex', justifyContent: 'space-between' }}
      >
        <Typography variant="body2" color="white">
          {t(titleKey, { defaultValue: defaultTitle })}
        </Typography>
      </CardActionArea>
    </Card>
  );
}

// ── Row actions menu ──────────────────────────────────────────────────────────

function ActionsMenu({
  record,
  onAction,
}: {
  record: AddWithdrawFundsRecord;
  onAction: (record: AddWithdrawFundsRecord, action: 'confirm' | 'reject') => void;
}) {
  const { t } = useTranslation();
  const { hasAny } = usePermissions();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const canConfirm = hasAny(['edit:ops_confirmUserOp', 'ADMIN']);
  const canReject = hasAny(['edit:ops_rejectUserOp', 'ADMIN']);
  if (!canConfirm && !canReject) return null;

  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {canConfirm && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setAnchor(null);
              onAction(record, 'confirm');
            }}
          >
            <CheckIcon fontSize="small" sx={{ mr: 1 }} />
            {t('pages.userOperations.actions.confirm', { defaultValue: 'Confirm' })}
          </MenuItem>
        )}
        {canReject && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setAnchor(null);
              onAction(record, 'reject');
            }}
          >
            <CloseIcon fontSize="small" sx={{ mr: 1 }} />
            {t('pages.userOperations.actions.reject', { defaultValue: 'Reject' })}
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function UserOperationsPage() {
  const { t } = useTranslation();
  const { hasAny } = usePermissions();
  const { open: openDialog } = useDialog();
  const qc = useQueryClient();

  // ── filter state ────────────────────────────────────────────────────────────
  const today = moment();
  const [filterType, setFilterType] = useState('ADD_FUNDS');
  const [stagedStart, setStagedStart] = useState<Moment>(moment(today).startOf('month'));
  const [stagedEnd, setStagedEnd] = useState<Moment>(moment(today).add(1, 'month').startOf('month'));
  const [appliedRequest, setAppliedRequest] = useState({
    type: 'ADD_FUNDS',
    startDate: fmt(moment(today).startOf('month')),
    endDate: fmt(moment(today).add(1, 'month').startOf('month')),
  });
  const [textFilter, setTextFilter] = useState('');
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // ── queries ─────────────────────────────────────────────────────────────────
  const recordsQuery = useUserOperationsQuery(appliedRequest);
  const statsQuery = useUserOperationsStatsQuery(OperationsStatusEnum.PENDING);
  const piQuery = usePaymentIntermediariesQuery(PaymentIntermediariesStatusEnum.ACTIVE);

  const stats = statsQuery.data;
  const paymentIntermediaries = piQuery.data ?? [];

  // ── derived data ─────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const records = recordsQuery.data ?? [];
    if (!textFilter.trim()) return records;
    const term = textFilter.toLowerCase();
    return records.filter(
      (r) =>
        r.uuid?.toLowerCase().includes(term) ||
        r.businessEntityName?.toLowerCase().includes(term) ||
        r.status?.toLowerCase().includes(term) ||
        r.amountRequested?.toString().includes(term) ||
        r.processingFee?.toString().includes(term),
    );
  }, [recordsQuery.data, textFilter]);

  const sorted = useMemo(() => {
    const asc = sortDir === 'asc';
    return [...filtered].sort((a, b) => {
      switch (sortField) {
        case 'reference':   return compare(a.uuid, b.uuid, asc);
        case 'date':        return compare(a.date, b.date, asc);
        case 'businessName':return compare(a.businessEntityName ?? '', b.businessEntityName ?? '', asc);
        case 'status':      return compare(a.status, b.status, asc);
        case 'amount':      return compare(a.amountRequested ?? 0, b.amountRequested ?? 0, asc);
        default:            return 0;
      }
    });
  }, [filtered, sortField, sortDir]);

  const rowsPerPage = 10;
  const paged = sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // ── handlers ─────────────────────────────────────────────────────────────────
  const handleTypeChange = (_: React.MouseEvent<HTMLElement>, newType: string | null) => {
    if (!newType) return;
    setFilterType(newType);
    setTextFilter('');
    setPage(0);
    setAppliedRequest((prev) => ({ ...prev, type: newType }));
  };

  const handleApply = () => {
    setTextFilter('');
    setPage(0);
    setAppliedRequest({ type: filterType, startDate: fmt(stagedStart), endDate: fmt(stagedEnd) });
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleRowClick = (record: AddWithdrawFundsRecord) => {
    if (record.status.toLowerCase() !== 'pending') {
      void openDialog<AddWithdrawFundsRecord | null>((close) => (
        <OperationDetailsDialog
          record={record}
          actionType="view"
          paymentIntermediaries={paymentIntermediaries}
          onClose={close}
        />
      ));
    }
  };

  const handleAction = async (record: AddWithdrawFundsRecord, action: 'confirm' | 'reject') => {
    const result = await openDialog<AddWithdrawFundsRecord | null>((close) => (
      <OperationDetailsDialog
        record={record}
        actionType={action}
        paymentIntermediaries={paymentIntermediaries}
        onClose={close}
      />
    ));
    if (result) {
      void qc.invalidateQueries({ queryKey: USER_OPS_KEYS.stats(OperationsStatusEnum.PENDING) });
      await Swal.fire({
        position: 'top-end',
        icon: 'success',
        text: t('messages.record-updated-successfully', { defaultValue: 'Record updated successfully' }),
        showConfirmButton: false,
        timer: 1500,
      });
    }
  };

  // ── export ────────────────────────────────────────────────────────────────────
  const exportFile = (type: 'csv' | 'xls') => {
    const rows = recordsQuery.data ?? [];
    if (!rows.length) return;

    const toExtract = rows.map((uo) => ({
      [t('pages.userOperations.table.header.reference', { defaultValue: 'Reference' })]: uo.uuid,
      [t('pages.userOperations.table.header.date', { defaultValue: 'Date' })]: moment(uo.date).format('YYYY-MM-DD'),
      [t('pages.userOperations.table.header.businessName', { defaultValue: 'Business Name' })]:
        uo.orderType === 'ADDRESS_BOOK_TRANSFER'
          ? uo.contactType === 'INDIVIDUAL' ? '' : uo.businessEntityName
          : uo.businessEntityName,
      [t('pages.userOperations.dialog.address', { defaultValue: 'Address' })]:
        uo.orderType === 'ADDRESS_BOOK_TRANSFER'
          ? uo.contactType === 'INDIVIDUAL' ? '' : uo.address ?? ''
          : uo.address ?? '',
      [t('pages.userOperations.table.header.orderType', { defaultValue: 'Order Type' })]: uo.orderTypeDescription,
      [t('pages.userOperations.table.header.status', { defaultValue: 'Status' })]: uo.statusDescription,
      [t('pages.userOperations.dialog.amountToPay', { defaultValue: 'Amount to Pay' })]: uo.amountRequested,
      [t('pages.userOperations.dialog.amountPaid', { defaultValue: 'Amount Paid' })]:
        uo.orderType === 'ADD_FUNDS'
          ? uo.amountRequested - uo.processingFee
          : uo.amountRequested + uo.processingFee,
      [t('pages.userOperations.dialog.remark', { defaultValue: 'Remark' })]: uo.remark ?? '',
      [t('pages.userOperations.dialog.firstname', { defaultValue: 'First Name' })]: uo.firstName ?? '',
      [t('pages.userOperations.dialog.lastname', { defaultValue: 'Last Name' })]: uo.lastName ?? '',
      [t('pages.userOperations.dialog.paymentIntermediary', { defaultValue: 'Payment Intermediary' })]:
        uo.status !== 'PENDING'
          ? (!Array.isArray(uo.paymentIntermediary) && uo.paymentIntermediary?.name) ?? ''
          : '',
      [t('pages.userOperations.dialog.accountNumber', { defaultValue: 'Account Number' })]:
        uo.status !== 'PENDING'
          ? (!Array.isArray(uo.paymentIntermediary) && uo.paymentIntermediary?.accountNumber) ?? ''
          : '',
    }));

    let blob: Blob;
    const filename = `uops_${filterType}_${appliedRequest.startDate}_${appliedRequest.endDate}.${type}`;

    if (type === 'csv') {
      const header = Object.keys(toExtract[0]);
      const csv = toExtract
        .map((row) =>
          header.map((k) => JSON.stringify((row as Record<string, unknown>)[k] ?? '')).join(','),
        )
        .join('\r\n');
      blob = new Blob([header.join(',') + '\r\n' + csv], { type: 'text/csv' });
    } else {
      const ws = XLSX.utils.json_to_sheet(toExtract);
      const wb: XLSX.WorkBook = { Sheets: { data: ws }, SheetNames: ['data'] };
      const buf: ArrayBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
      blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      });
    }

    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
    a.remove();
  };

  // ── export menu ────────────────────────────────────────────────────────────
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  // ── render ────────────────────────────────────────────────────────────────────
  const canViewStats = hasAny(['view:ops_statsUserOps']);
  const canViewList = hasAny(['view:ops_listUserOps', 'ADMIN']);
  const canTakeAction = hasAny(['edit:ops_confirmUserOp', 'edit:ops_rejectUserOp', 'ADMIN']);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('menus.userOperationNavigation', { defaultValue: 'User Operations' })}
      </Typography>

      {/* ── Stats widgets ─────────────────────────────────────────────────── */}
      {canViewStats && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
          <StatCard
            count={stats?.addFunds?.pending ?? 0}
            contentKey="pages.userOperations.widget1.content"
            defaultContent="Pending add funds"
            titleKey="pages.userOperations.widget1.title"
            defaultTitle="Add Funds"
            color="#4CAF50"
            onClick={() => handleTypeChange(null as unknown as React.MouseEvent<HTMLElement>, 'ADD_FUNDS')}
          />
          <StatCard
            count={stats?.withdrawFunds?.pending ?? 0}
            contentKey="pages.userOperations.widget2.content"
            defaultContent="Pending withdraw funds"
            titleKey="pages.userOperations.widget2.title"
            defaultTitle="Withdraw Funds"
            color="#E91E63"
            onClick={() => handleTypeChange(null as unknown as React.MouseEvent<HTMLElement>, 'WITHDRAW_FUNDS')}
          />
          <StatCard
            count={stats?.bankTransfer?.pending ?? 0}
            contentKey="pages.userOperations.widget3.content"
            defaultContent="Pending bank transfers"
            titleKey="pages.userOperations.widget3.title"
            defaultTitle="Bank Transfer"
            color="#4f9aab"
            onClick={() => handleTypeChange(null as unknown as React.MouseEvent<HTMLElement>, 'ADDRESS_BOOK_TRANSFER')}
          />
          <StatCard
            count={stats?.withdrawRevenue?.pending ?? 0}
            contentKey="pages.userOperations.widget4.content"
            defaultContent="Pending withdraw revenue"
            titleKey="pages.userOperations.widget4.title"
            defaultTitle="Withdraw Revenue"
            color="#9E9E9E"
            onClick={() => handleTypeChange(null as unknown as React.MouseEvent<HTMLElement>, 'WITHDRAW_REVENUE')}
          />
        </Stack>
      )}

      {/* ── Records table ─────────────────────────────────────────────────── */}
      {canViewList && (
        <Paper sx={{ p: 2 }}>
          {/* Type filter toggle */}
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={filterType}
              exclusive
              onChange={handleTypeChange}
              size="small"
            >
              {FILTER_TYPES.map((ft) => (
                <ToggleButton key={ft.key} value={ft.key}>
                  {t(ft.labelKey, { defaultValue: ft.defaultLabel })}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Date range + export + text filter */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <DatePicker
                label={t('pages.userOperations.startDate', { defaultValue: 'Start Date' })}
                value={stagedStart}
                onChange={(v) => v && setStagedStart(v)}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
              />
              <DatePicker
                label={t('pages.userOperations.endDate', { defaultValue: 'End Date' })}
                value={stagedEnd}
                onChange={(v) => v && setStagedEnd(v)}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
              />
              <Button variant="contained" size="small" onClick={handleApply}>
                {t('global.buttons.apply', { defaultValue: 'Apply' })}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                disabled={!recordsQuery.data?.length}
                onClick={(e) => setExportAnchor(e.currentTarget)}
              >
                {t('global.buttons.export', { defaultValue: 'Export' })}
              </Button>
              <Menu
                anchorEl={exportAnchor}
                open={Boolean(exportAnchor)}
                onClose={() => setExportAnchor(null)}
              >
                <MenuItem onClick={() => { exportFile('csv'); setExportAnchor(null); }}>
                  {t('global.buttons.csv_export', { defaultValue: 'CSV Export' })}
                </MenuItem>
                <MenuItem onClick={() => { exportFile('xls'); setExportAnchor(null); }}>
                  {t('global.buttons.xls_export', { defaultValue: 'XLS Export' })}
                </MenuItem>
              </Menu>
            </Stack>
            <TextField
              size="small"
              value={textFilter}
              onChange={(e) => { setTextFilter(e.target.value); setPage(0); }}
              placeholder={t('pages.userOperations.filter', { defaultValue: 'Filter…' })}
              sx={{ minWidth: 220 }}
            />
          </Stack>

          {/* Table */}
          {recordsQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : recordsQuery.isError ? (
            <Typography color="error" sx={{ p: 2 }}>
              {t('messages.unknown-error', { defaultValue: 'An error occurred loading records.' })}
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sortDirection={sortField === 'reference' ? sortDir : false}>
                        <TableSortLabel
                          active={sortField === 'reference'}
                          direction={sortField === 'reference' ? sortDir : 'asc'}
                          onClick={() => handleSort('reference')}
                        >
                          {t('pages.userOperations.table.header.reference', { defaultValue: 'Reference' })}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sortDirection={sortField === 'date' ? sortDir : false}>
                        <TableSortLabel
                          active={sortField === 'date'}
                          direction={sortField === 'date' ? sortDir : 'asc'}
                          onClick={() => handleSort('date')}
                        >
                          {t('pages.userOperations.table.header.date', { defaultValue: 'Date' })}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="left" sortDirection={sortField === 'businessName' ? sortDir : false}>
                        <TableSortLabel
                          active={sortField === 'businessName'}
                          direction={sortField === 'businessName' ? sortDir : 'asc'}
                          onClick={() => handleSort('businessName')}
                        >
                          {t('pages.userOperations.table.header.businessName', { defaultValue: 'Business Name' })}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="left">
                        {t('pages.userOperations.table.header.orderType', { defaultValue: 'Order Type' })}
                      </TableCell>
                      <TableCell align="center" sortDirection={sortField === 'status' ? sortDir : false}>
                        <TableSortLabel
                          active={sortField === 'status'}
                          direction={sortField === 'status' ? sortDir : 'asc'}
                          onClick={() => handleSort('status')}
                        >
                          {t('pages.userOperations.table.header.status', { defaultValue: 'Status' })}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sortDirection={sortField === 'amount' ? sortDir : false}>
                        <TableSortLabel
                          active={sortField === 'amount'}
                          direction={sortField === 'amount' ? sortDir : 'asc'}
                          onClick={() => handleSort('amount')}
                        >
                          {t('pages.userOperations.table.header.amount', { defaultValue: 'Amount' })}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">
                        {t('pages.userOperations.table.header.actions', { defaultValue: 'Actions' })}
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paged.map((record) => {
                      const isPending = record.status.toLowerCase() === 'pending';
                      const isProcessed = record.status.toLowerCase() === 'processed';
                      const statusColor: 'warning' | 'success' | 'error' = isPending
                        ? 'warning'
                        : isProcessed
                        ? 'success'
                        : 'error';

                      const displayName =
                        record.orderType === 'ADDRESS_BOOK_TRANSFER' && record.contactType !== 'BUSINESS'
                          ? t('pages.userOperations.dialog.not_specified', { defaultValue: 'N/A' })
                          : record.businessEntityName;

                      return (
                        <TableRow
                          key={record.id ?? record.uuid}
                          hover
                          onClick={() => handleRowClick(record)}
                          sx={{ cursor: isPending ? 'default' : 'pointer' }}
                        >
                          <TableCell align="center">
                            <Tooltip title={record.uuid}>
                              <Typography variant="body2" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {record.uuid}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {moment(record.date).format('DD MMM YYYY')}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {t('pages.userOperations.at', { defaultValue: 'at' })}{' '}
                              {moment(record.date).format('HH:mm')}
                            </Typography>
                          </TableCell>
                          <TableCell align="left">{displayName}</TableCell>
                          <TableCell align="left">
                            {t(`pages.userOperations.orderTypes.${record.orderType}`, {
                              defaultValue: record.orderTypeDescription ?? record.orderType,
                            })}
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={record.status} color={statusColor} size="small" />
                          </TableCell>
                          <TableCell align="center">
                            XAF {record.amountRequested?.toLocaleString()}
                          </TableCell>
                          <TableCell align="center">
                            {isPending && canTakeAction && (
                              <ActionsMenu record={record} onAction={handleAction} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {paged.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            {t('messages.no-data', { defaultValue: 'No data' })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={sorted.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
                labelDisplayedRows={({ from, to, count }) =>
                  t('pages.userOperations.table.pagination.range', {
                    defaultValue: `${from}–${to} of ${count}`,
                    from,
                    to,
                    count,
                  })
                }
                labelRowsPerPage={t('pages.userOperations.table.pagination.rowsPerPage', { defaultValue: 'Rows per page:' })}
              />
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}
